import React, {
  createContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import "./App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import CheckoutPage from "./Pages/ComponentsCheckoutPage/CheckoutPage";
import PaymentSuccessful from "./Pages/ComponentsPaymentSuccessful/PaymentSuccessful";
import PaymentFailed from "./Pages/ComponentsPaymentFailed/PaymentFailed";
import LinkUnavailable from "./Pages/ComponentsLinkUnavailable/LinkUnavailable";
import PaymentPage from "./Pages/ComponentsCheckoutPage/PaymentPage";

import {
  trackData,
  getSessionId,
} from "./Utils/attribution/CheckoutAttribution";
import SummaryModal from "./Pages/ComponentsCheckoutPage/Components/Modal/SummaryModal";
import { ItemDetailsData } from "./Utils/DefinitionsInterfaces";
import ReactGA from "react-ga4";
import "./overrides";
export const AuthContext = createContext({} as any);

interface UserDetails {
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  address: string;
  flatNo: string;
  city: string;
  state: string;
  pincode: string;
  paymentType: "prepaid" | "cod";
  schoolName: string;
  grade: number;
}

interface BackendSchool {
  name: string;
  city: string;
  state: string;
  examDate: string;
  isInternational: boolean;
  schoolID: number;
  version_no: "V1" | "V2";
  schoolCode: string;
  pg: "razorpay" | "billdesk";
}

interface CartContextType {
  userDetails: UserDetails;
  setUserDetails: Dispatch<SetStateAction<UserDetails>>;
  backendSchool: BackendSchool;
  setBackendSchool: Dispatch<SetStateAction<BackendSchool>>;
  schoolID: number;
  setSchoolID: Dispatch<SetStateAction<number>>;
  individualReg: boolean;
  setIndividualReg: Dispatch<SetStateAction<boolean>>;
  examDate: string;
  setExamDate: Dispatch<SetStateAction<string>>;
  isOtpVerified: boolean;
  setIsOtpVerified: Dispatch<SetStateAction<boolean>>;
  checkOutItems: ItemDetailsData[];
  setCheckOutItems: Dispatch<SetStateAction<ItemDetailsData[]>>;
  nfoSelected: boolean;
  setNfoSelected: Dispatch<SetStateAction<boolean>>;
  bundleInfo: {
    isBundle: boolean;
    primaryProductId: number | null;
  };
  setBundleInfo: Dispatch<
    SetStateAction<{
      isBundle: boolean;
      primaryProductId: number | null;
    }>
  >;
}

export const CartContext = createContext<CartContextType>({
  userDetails: {
    email: "",
    firstName: "",
    lastName: "",
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
    paymentType: "prepaid",
    schoolName: "",
    grade: 0,
  },
  setUserDetails: () => {},
  backendSchool: {
    name: "",
    city: "",
    state: "",
    examDate: "",
    isInternational: false,
    schoolID: 0,
    version_no: "V1",
    schoolCode: "",
    pg: "razorpay",
  },
  setBackendSchool: () => {},
  schoolID: 0,
  setSchoolID: () => {},
  individualReg: false,
  setIndividualReg: () => {},
  examDate: "",
  setExamDate: () => {},
  isOtpVerified: false,
  setIsOtpVerified: () => {},
  checkOutItems: [],
  setCheckOutItems: () => {},
  nfoSelected: false,
  setNfoSelected: () => {},
  bundleInfo: {
    isBundle: false,
    primaryProductId: null,
  },
  setBundleInfo: () => {},
});

const AppRoutes = () => {
  console.log("Environment => ", process.env);
  const setParams = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const itemIdsStorage = sessionStorage.getItem("item_ids");
    const accessToken = urlParams.get("token");
    const referredBy = urlParams.get("referredBy");
    const itemIdsUrlParams = urlParams.get("item_ids");
    if (accessToken) {
      sessionStorage.setItem("accessToken", accessToken);
    }
    if (itemIdsUrlParams && itemIdsStorage && itemIdsStorage?.length < 0) {
      sessionStorage.setItem("item_ids", itemIdsUrlParams);
    }
    if (referredBy) {
      sessionStorage.setItem("referredBy", referredBy);
    }
    sessionStorage.setItem("pg", "razorpay");
  };

  useEffect(() => {
    trackData("user_viewed_books_checkoutpage", null, null, {
      success: true,
      payment_type: "page_view",
    });
    // ReactGA.event({
    //   category: "user_viewed_books_checkoutpage",
    //   action: "user_viewed_books_checkoutpage",
    //   label: "user_viewed_books_checkoutpage",
    // });
    setParams();
  }, []);

  const CartProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    const [backendSchool, setBackendSchool] = useState<{
      name: string;
      city: string;
      state: string;
      examDate: string;
      isInternational: boolean;
      schoolID: number;
      version_no: "V1" | "V2";
      schoolCode: string;
      pg: "razorpay" | "billdesk";
    }>({
      name: "",
      city: "",
      state: "",
      examDate: "",
      isInternational: false,
      schoolID: 0,
      version_no: "V1",
      schoolCode: "",
      pg: "razorpay",
    });
    const [checkOutItems, setCheckOutItems] = useState<ItemDetailsData[]>([]);
    const [nfoSelected, setNfoSelected] = useState<boolean>(false);
    const [userDetails, setUserDetails] = useState<UserDetails>({
      email: "",
      firstName: "",
      lastName: "",
      mobileNumber: "",
      address: "",
      flatNo: "",
      city: "",
      state: "",
      pincode: "",
      paymentType: "prepaid",
      schoolName: "",
      grade: 0,
    });

    useEffect(() => {
      const keys: (keyof UserDetails)[] = [
        "email",
        "mobileNumber",
        "address",
        "flatNo",
        "city",
        "state",
        "pincode",
        "paymentType",
        "firstName",
        "lastName",
        "schoolName",
        "grade",
      ];

      ReactGA.initialize([
        {
          trackingId: "G-ZGFSQKYL39",
          gaOptions: {
            debug_mode: true,
          },
        },
        {
          trackingId: "GT-5D48XGC",
          gaOptions: {
            debug_mode: true,
          },
        },
      ]);

      // Set user_id using session_id from cookies for GA4 user identification
      (async () => {
        const sessionId = await getSessionId();
        console.log({ sessionId });
        if (sessionId) {
          ReactGA.set({ userId: sessionId });
          console.log("GA4 user_id set to session_id:", sessionId);
        }
      })();
      console.log("environment", process.env);

      const storedUserDetails = keys.reduce((details, key) => {
        const value = sessionStorage.getItem(key);
        if (value) {
          if (key === "paymentType") {
            if (value === "prepaid" || value === "cod") {
              details[key] = value;
            }
          } else if (key === "grade") {
            details[key] = parseInt(value) || 0;
          } else {
            details[key] = value;
          }
        }
        return details;
      }, {} as { [K in keyof UserDetails]: UserDetails[K] });

      setUserDetails((prevDetails) => ({
        ...prevDetails,
        ...storedUserDetails,
      }));
    }, []);

    const [schoolID, setSchoolID] = useState(0);
    const [individualReg, setIndividualReg] = useState(false);
    const [examDate, setExamDate] = useState("");
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [bundleInfo, setBundleInfo] = useState<{
      isBundle: boolean;
      primaryProductId: number | null;
    }>({
      isBundle: false,
      primaryProductId: null,
    });

    return (
      <CartContext.Provider
        value={{
          userDetails,
          setUserDetails,
          backendSchool,
          setBackendSchool,
          schoolID,
          setSchoolID,
          individualReg,
          setIndividualReg,
          examDate,
          setExamDate,
          isOtpVerified,
          setIsOtpVerified,
          checkOutItems,
          setCheckOutItems,
          nfoSelected,
          setNfoSelected,
          bundleInfo,
          setBundleInfo,
        }}
      >
        {children}
      </CartContext.Provider>
    );
  };

  return (
    <CartProvider>
      <BrowserRouter basename="/">
        <Routes>
          <Route path="*" element={<Navigate to={"/CheckoutPage"} replace />} />
          <Route path="/CheckoutPage" element={<CheckoutPage />} />
          <Route path="/PaymentSuccessful" element={<PaymentSuccessful />} />
          <Route path="/PaymentFailed" element={<PaymentFailed />} />
          <Route path="LinkUnavailable" element={<LinkUnavailable />} />
          <Route path="/PaymentPage" element={<PaymentPage />} />
          <Route path="/SummaryPage" element={<SummaryModal />} />
        </Routes>
      </BrowserRouter>
    </CartProvider>
  );
};

const App = () => {
  const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    return <AuthContext.Provider value={{}}>{children}</AuthContext.Provider>;
  };
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
    // <ServerDown />
  );
};

export default App;
